// app/components/AdvancedChart/index.tsx
'use client'

import React, { useRef, useEffect, useState, useCallback } from 'react'
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  UTCTimestamp,
  LineStyle,
  ISeriesApi,
  Time
} from 'lightweight-charts'

// Our hooks for chart data and orders.
import { useChartData } from './hooks/useChartData'
import { useOrders } from './hooks/useOrders'

// New hooks for chart initialization, data updates, user script indicator,
// built-in indicator plotting, and action handlers.
import { useInitializeCharts } from './hooks/useInitializeCharts'
import { useUpdateChartData } from './hooks/useUpdateChartData'
import { useUserScriptIndicator } from './hooks/useUserScriptIndicator'
import { useBuiltInIndicator } from './hooks/useBuiltInIndicator'
import { useChartActions } from './hooks/useChartActions'

// Our sub-components.
import { SymbolSearchModal } from '../SymbolSearch/SymbolSearch'
import { IndicatorPickerButton } from './components/IndicatorPicker'
import TradingModal from './components/TradingModal'

// Our constants.
import { MAIN_CHART_CONFIG } from './constants/chartConfig'

// Price formatting utility
import { formatPrice } from './utils/priceFormatter'

// For custom utilities.
import { syncTimeScale } from './utils/syncTimeScale'
import { toUTCTimestamp } from './utils/time'

// WebSocket hook for real-time updates.
import { useWebSocket } from './hooks/useWebSocket'
import type { AdvancedChartProps } from './types'
import {  Candle } from './types'
import AnalyticsTable from './components/AnalyticsTable'

// Import professional drawing system
import DrawingManager from './components/DrawingManager'
import type { Drawing } from './types/drawing'
import type { DrawingManagerRef } from './components/DrawingManager'

// Import AI Analysis components
import AIAnalysisModal from './components/AIAnalysisModal'
import { useAIAnalysis } from './hooks/useAIAnalysis'

export default function AdvancedChart({
  data,
  symbol,
  mainRatio = 70,
  rsiRatio = 30,
  userScriptCode,
  theme,
  openOrders: urlOpenOrders
}: AdvancedChartProps) {
  // 1) Chart data state.
  const {
    currentSymbol,
    setCurrentSymbol,
    chartDataLocal,
    setChartDataLocal,
    timeframe,
    setTimeframe
  } = useChartData(data, symbol)

  // 2) Orders state.
  // Use URL-provided orders if available, otherwise fetch from API/connectors
  const { openOrders: fetchedOpenOrders, openOrderLines, setOpenOrderLines } = useOrders()
  const openOrders = urlOpenOrders || fetchedOpenOrders

  // 3) Symbol picker states.
  const [symbolPickerOpen, setSymbolPickerOpen] = useState(false)
  const [newSymbol, setNewSymbol] = useState(currentSymbol)

  // 4) Indicator picker state - no longer needed with native approach
  const [selectedIndicator, setSelectedIndicator] = useState<string | null>(null)
  const [activeIndicators, setActiveIndicators] = useState<string[]>([])

  // 5) FX Compare states.
  const [fxPickerOpen, setFxPickerOpen] = useState(false)
  const [fxNewSymbol, setFxNewSymbol] = useState(currentSymbol)
  const [fxSymbol, setFxSymbol] = useState<string | null>(null)
  const fxSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)
  // reuse useChartData hook for FX history
  const { chartDataLocal: fxChartDataLocal } = useChartData([], fxSymbol || '', timeframe)

  // 6) Refs for container and main chart.
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mainChartContainer = useRef<HTMLDivElement | null>(null)

  // 7) Ref for the crosshair popup.
  const popupRef = useRef<HTMLDivElement | null>(null)

  // 8) Ref for the DrawingManager to clear drawings on symbol change
  const drawingManagerRef = useRef<DrawingManagerRef | null>(null)

  // 8) Crosshair popup state.
  const [popupVisible, setPopupVisible] = useState(false)
  const [popupX, setPopupX] = useState(0)
  const [popupY, setPopupY] = useState(0)
  const [price, setPrice] = useState<number | null>(null)
  const [percentChange, setPercentChange] = useState('')
  const [lastPrice, setLastPrice] = useState<number | null>(null)
  const lastPriceRef = useRef<number | null>(null)

  // OHLC data state for crosshair display
  const [ohlcData, setOhlcData] = useState<{
    open: number;
    high: number;
    low: number;
    close: number;
  } | null>(null)


  // 9) Action toggle state.
  const [actionsVisible, setActionsVisible] = useState(false)

  // Log scale state
  const [isLogScale, setIsLogScale] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(true)

  // ─── PROFESSIONAL DRAWING SYSTEM ────────────────────────────────────────────
  // Modern drawing tools with Fibonacci, Channels, and more
  const [showDrawingTools, setShowDrawingTools] = useState(false)

  // ─── AI ANALYSIS SYSTEM ──────────────────────────────────────────────────────
  // AI-powered chart analysis with GPT-4o vision
  const {
    isModalOpen: isAIModalOpen,
    loading: aiLoading,
    error: aiError,
    analysis: aiAnalysis,
    analyzeChart,
    retryAnalysis,
    closeModal: closeAIModal
  } = useAIAnalysis({
    symbol: currentSymbol,
    timeframe,
    currentPrice: lastPrice || undefined
  })
  
  // 9) Update new symbol when currentSymbol changes.
  useEffect(() => {
    setNewSymbol(currentSymbol.toUpperCase())
    setFxNewSymbol(currentSymbol.toUpperCase())
  }, [currentSymbol])

  // Clear drawings when symbol changes to prevent crashes
  useEffect(() => {
    if (drawingManagerRef.current) {
      drawingManagerRef.current.clearAllDrawings()
    }
  }, [currentSymbol])

  // 12) Listen for theme changes and update the chart options.
  useEffect(() => {
    const handleThemeChange = (e: CustomEvent<{ isDark: boolean }>) => {
      if (chartRef.current) {
        // Reapply the main chart config with the new theme.
        chartRef.current.applyOptions(MAIN_CHART_CONFIG(e.detail.isDark))
      }
    }
    window.addEventListener('themeChange', handleThemeChange as EventListener)
    return () => {
      window.removeEventListener('themeChange', handleThemeChange as EventListener)
    }
  }, [])

  // === Mouse event handlers ===
  function onMouseLeave() {
    setPopupVisible(false)
  }
  function onMouseEnter() {
    // No additional action.
  }

  // === Chart resizing handler ===
  function handleResize() {
    if (!containerRef.current || !chartRef.current) return
    const totalH = containerRef.current.clientHeight
    const totalW = containerRef.current.clientWidth
    // Our main chart fills the entire container.
    chartRef.current.applyOptions({ width: totalW, height: totalH })
    chartRef.current.timeScale().fitContent()
  }

  // === Step 1: Initialize charts using our custom hook ===
  const { chartRef, mainSeriesRef, volumeSeriesRef } = useInitializeCharts({
    currentSymbol,
    mainChartContainer,
    containerRef,
    popupRef,
    popupVisible,
    setPopupVisible,
    setPrice,
    setPercentChange,
    setPopupX,
    setPopupY,
    lastPriceRef,
    onMouseLeave,
    onMouseEnter,
    handleResize,
    theme,
    chartDataLocal,
    setOhlcData
  })

  // Update price scale mode when log scale toggle changes or symbol changes
  useEffect(() => {
    if (mainSeriesRef.current) {
      mainSeriesRef.current.applyOptions({
        priceFormat: {
          type: 'custom',
          formatter: formatPrice,
        }
      })
      mainSeriesRef.current.priceScale().applyOptions({
        scaleMargins: {
          top: 0.1,
          bottom: 0.2
        }
      })
      const priceScale = mainSeriesRef.current.priceScale()
      priceScale.applyOptions({
        mode: isLogScale ? 2 : 0, // 2 for logarithmic, 0 for normal
      })
    }
  }, [isLogScale, currentSymbol])

  // Update timeScale formatting based on timeframe
  useEffect(() => {
    if (chartRef.current) {
      // For shorter timeframes (4h and below), show both date and time
      const shouldShowDateTime = ['1m', '5m', '15m', '30m', '1h', '2h', '4h'].includes(timeframe)
      
      if (shouldShowDateTime) {
        chartRef.current.applyOptions({
          timeScale: {
            timeVisible: true,
            secondsVisible: false,
          }
        })
      } else {
        // For longer timeframes, use default formatting (date only)
        chartRef.current.applyOptions({
          timeScale: {
            timeVisible: false,
            secondsVisible: false,
          }
        })
      }
    }
  }, [timeframe, chartRef])

  // === Step 2: Update chart data and plot orders ===
  useUpdateChartData({
    chartData: chartDataLocal,
    openOrders,
    currentSymbol,
    mainSeriesRef,
    volumeSeriesRef,
    setLastPrice,
    lastPriceRef,
    setOpenOrderLines
  })

  // === Step 3: FX Compare: plot FX history when symbol/data change ===
  useEffect(() => {
    const chart = chartRef.current!

    // remove existing FX series if symbol cleared
    if (!fxSymbol) {
      if (fxSeriesRef.current) {
        chart.removeSeries(fxSeriesRef.current)
        fxSeriesRef.current = null
      }
      return
    }

    // add FX series if not present
    if (!fxSeriesRef.current) {
      fxSeriesRef.current = chart.addSeries(LineSeries, {
        color: '#FF9900',
        lineWidth: 2,
        lineStyle: LineStyle.Solid
      }) as ISeriesApi<'Line'>
    }

    // map, filter, and sort FX data
    const pts = fxChartDataLocal
      .map(c => {
        const timeNum = typeof c.time === 'number'
          ? c.time
          : Math.floor(new Date(c.time).getTime() / 1000)
        return { time: toUTCTimestamp(timeNum as Time) || (timeNum as UTCTimestamp), value: (c as any).close }
      })
      .filter(pt => !isNaN(pt.time))
      .sort((a, b) => a.time - b.time)

    fxSeriesRef.current.setData(pts)
  }, [fxSymbol, fxChartDataLocal])

  // === Step 4: WebSocket updates for FX history ===
  useWebSocket(fxSymbol || '', timeframe, newFx => {
    if (fxSeriesRef.current) {
      const fmt = {
        time: typeof newFx.time === 'number' ? newFx.time : Number(newFx.time),
        value: newFx.close
      }
      fxSeriesRef.current.update(fmt)
    }
  })

  // === Step 5: Execute user script indicator logic ===
  useUserScriptIndicator({
    userScriptCode,
    chartData: chartDataLocal,
    mainChartRef: chartRef
  })

  // === Step 6: Plot built-in indicator using panes if needed ===
  const { indicatorSeries, alertInfo } = useBuiltInIndicator(
    chartRef.current,
    chartDataLocal,
    selectedIndicator
  )

  // === Step 7: Get action handlers ===
  const { 
    toggleActions, 
    handleBuy, 
    handleSell, 
    handleDraw, 
    isTradingModalOpen,
    handleCloseModal,
    tradingAction 
  } = useChartActions({
    price,
    mainChartRef: chartRef,
    chartData: chartDataLocal,
    setActionsVisible,
    currentSymbol
  })

  // === WebSocket updates for real-time data ===
  useWebSocket(currentSymbol, timeframe, newCandle => {
    if (mainSeriesRef.current) {
      try {
        // The newCandle.time is already normalized by useWebSocket
        const formattedCandle = {
          ...newCandle,
          time: toUTCTimestamp(newCandle.time) || (newCandle.time as UTCTimestamp) // Properly convert time
        };

        if (mainSeriesRef.current) {
          mainSeriesRef.current.update(formattedCandle);
          setLastPrice(formattedCandle.close);
          lastPriceRef.current = formattedCandle.close;
        }
        
        // Update chartDataLocal as well to keep it in sync
        setChartDataLocal((prevData: Candle[]) => {
          const existingCandleIndex = prevData.findIndex(d => d.time === formattedCandle.time);
          if (existingCandleIndex !== -1) {
            // Update existing candle
            const newData = [...prevData];
            newData[existingCandleIndex] = formattedCandle;
            return newData;
          } else {
            // Add new candle if it's a new period (should be rare with normalization but good for safety)
            // Or if chartDataLocal was empty
            return [...prevData, formattedCandle].sort((a, b) => Number(a.time) - Number(b.time));
          }
        });
      } catch (error) {
        console.error('Error updating chart with WebSocket data:', error)
      }
    }
  })

  // === Professional Drawing system handlers ===
  const handleDrawingComplete = (drawing: Drawing) => {
    console.log('Drawing completed:', drawing)
    // You can save to backend here
    // saveDrawingToBackend(drawing)
  }

  const handleDrawingUpdate = (drawing: Drawing) => {
    console.log('Drawing updated:', drawing)
    // You can update backend here
    // updateDrawingInBackend(drawing)
  }

  const handleDrawingDelete = (drawingId: string) => {
    console.log('Drawing deleted:', drawingId)
    // You can delete from backend here
    // deleteDrawingFromBackend(drawingId)
  }

  // === Timeframe change handler ===
  const handleTimeframeChange = (newTimeframe: string) => {
    setTimeframe(newTimeframe)
    setActionsVisible(false)
  }

  // === AI Analysis handler ===
  const handleAIAnalysis = useCallback(() => {
    if (containerRef.current) {
      analyzeChart(containerRef.current)
    }
  }, [analyzeChart])

  // === Popup style ===
  const popupStyle: React.CSSProperties = {
    position: 'absolute',
    left: popupX + 100,
    top: popupY,
    background: '#c6dfeaf6',
    border: '1px solid #00000017',
    borderRadius: 5,
    boxShadow: '0 2px 8px rgba(166, 206, 219, 0.5)',
    display: popupVisible ? 'flex' : 'none',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '2px',
    zIndex: 9999,
    pointerEvents: popupVisible ? 'auto' : 'none'
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          flex: 1,
          overflow: 'hidden'
        }}
      >
      {/* Symbol + Indicator + FX + Draw Buttons */}
      <div
        style={{
          position: 'absolute',
          top: 6,
          left: 6,
          zIndex: 9999,
          backgroundColor: 'rgba(0,0,0,0.2)',
          padding: '2px 6px',
          borderRadius: 4,
          display: 'flex',
          gap: '8px'
        }}
      >
        {/* Symbol Dropdown */}
        <button
          style={{
            background: 'transparent',
            color: '#fff',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontSize: '0.95rem'
          }}
          onClick={() => setSymbolPickerOpen(true)}
        >
          {currentSymbol.toUpperCase()} ▼
        </button>
        {/* Indicator Picker */}
        <IndicatorPickerButton
          onSelectIndicator={(indicatorName: string) => {
            // If there's already a selected indicator, remove it from active list
            if (selectedIndicator) {
              setActiveIndicators(prev => prev.filter(ind => ind !== selectedIndicator))
            }
            
            setSelectedIndicator(indicatorName)
            // Add new indicator to active indicators if not already present
            setActiveIndicators(prev => 
              prev.includes(indicatorName) ? prev : [...prev, indicatorName]
            )
          }}
          activeIndicators={activeIndicators}
          onRemoveIndicator={indicatorName => {
            // Remove from active indicators
            setActiveIndicators(prev => prev.filter(ind => ind !== indicatorName))
            // If this was the selected indicator, clear it
            if (selectedIndicator === indicatorName) {
              setSelectedIndicator(null)
            }
          }}
        />
        {/* Timeframe Selector */}
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            style={{
              background: 'transparent',
              color: '#fff',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center'
            }}
            onClick={e => {
              const select = e.currentTarget.nextElementSibling as HTMLSelectElement
              select?.click()
            }}
          >
            {timeframe} ▼
          </button>
          <select
            value={timeframe}
            onChange={e => handleTimeframeChange(e.target.value)}
            style={{
              position: 'absolute',
              opacity: 0,
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              cursor: 'pointer'
            }}
          >
            <option value="1m">1m</option>
            <option value="5m">5m</option>
            <option value="15m">15m</option>
            <option value="1h">1h</option>
            <option value="4h">4h</option>
            <option value="1d">1d</option>
            <option value="1w">1w</option>
            <option value="1M">1M</option>
          </select>
        </div>
        {/* FX Compare */}
        <button
          onClick={() => { setFxNewSymbol(fxSymbol || currentSymbol); setFxPickerOpen(true) }}
          style={{ background: 'transparent', color: '#fff', border: 'none', padding: '0', cursor: 'pointer', fontSize: '0.95rem', display: 'flex', alignItems: 'center' }}
        >
          f<sub style={{ fontSize: '0.7rem', marginLeft: '-2px' }}>x</sub> ▼
        </button>
        {/* Professional Drawing Tools Toggle */}
        <button
          style={{
            background: showDrawingTools ? '#21728d' : 'transparent',
            color: '#fff',
            border: showDrawingTools ? '1px solid #88a1ac' : 'none',
            cursor: 'pointer',
            fontSize: '0.95rem',
            marginLeft: '0px',
            fontWeight: showDrawingTools ? 'bold' : 'normal',
            padding: showDrawingTools ? '2px 6px' : '0px',
            borderRadius: '4px',
            transition: 'all 0.2s ease'
          }}
          onClick={() => setShowDrawingTools(prev => !prev)}
          title="Toggle Drawing Tools (Fibonacci, Channels, Trend Lines)"
                  >
          {showDrawingTools ? 'Hide Tools' : '✎ᝰ'}
          </button>
      </div>

      {/* OHLC Data Display */}
      {ohlcData && (
        <div
          style={{
            position: 'absolute',
            top: 35,
            left: 6,
            zIndex: 9999,
            backgroundColor: 'rgba(0,0,0,0.2)',
            padding: '2px 6px',
            borderRadius: 4,
            fontSize: 8,
            color: '#fff',
            fontFamily: 'monospace',
            lineHeight: 1.2
          }}
        >
          O: {formatPrice(ohlcData.open)} | H: {formatPrice(ohlcData.high)} | L: {formatPrice(ohlcData.low)} | C: {formatPrice(ohlcData.close)}
        </div>
      )}

      {/* Chart Container */}
      <div 
        style={{ width: '100%', height: '100%', position: 'relative' }} 
        ref={mainChartContainer}
        data-chart-container
      >
        {/* Professional Drawing Manager */}
        <DrawingManager
          key={currentSymbol} // Force remount on symbol change to prevent stale references
          ref={drawingManagerRef}
          chart={chartRef.current}
          mainSeries={mainSeriesRef.current}
          isVisible={showDrawingTools} // Controls toolbar visibility, drawings always remain visible
          onDrawingComplete={handleDrawingComplete}
          onDrawingUpdate={handleDrawingUpdate}
          onDrawingDelete={handleDrawingDelete}
          onAIAnalysis={handleAIAnalysis} // Pass AI analysis handler
          chartData={chartDataLocal} // Pass chart data for pattern detection
        />

        {/* Log Scale Toggle */}
        <button
          onClick={() => setIsLogScale(!isLogScale)}
          style={{
            position: 'absolute',
            bottom: '10px',
            right: '10px',
            background: isLogScale ? '#88A1AC' : '#88A1AC33',
            border: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.7rem',
            zIndex: 2
          }}
        >
          {isLogScale ? 'Log' : 'Lin'}
        </button>
      </div>

      {/* Crosshair Popup */}
      <div ref={popupRef} style={popupStyle}>
        <button
          onClick={toggleActions}
          style={{
            background: actionsVisible ? 'none' : '#88a1ac',
            color: actionsVisible ? '#c0392b' : '#fff',
            width: 18,
            height: 18,
            borderRadius: '50%',
            textAlign: 'center',
            padding: 0,
            fontSize: actionsVisible ? 24 : 12,
            lineHeight: '18px',
            cursor: 'pointer',
            border: 'none',
            marginRight: 5,
            transform: actionsVisible ? 'rotate(45deg)' : 'none',
            transition: 'transform 0.2s ease'
          }}
        >
          +
        </button>
        {actionsVisible && (
          <div style={{ display: 'flex', flexDirection: 'column', marginRight: 6 }}>
            <button
              style={{ marginBottom: 5, background: '#16a085', color: '#fff' }}
              onClick={handleBuy}
            >
              Buy
            </button>
            <button
              style={{ marginBottom: 5, background: '#c0392b', color: '#fff' }}
              onClick={handleSell}
            >
              Sell
            </button>
            <button
              style={{ background: '#2980b9', color: '#fff' }}
              onClick={handleDraw}
            >
              Line
            </button>
          </div>
        )}
        <p style={{ margin: 0, fontSize: 10, color: '#000' }}>
          {price ? `${formatPrice(price)}` : ''}
          {percentChange ? (
            <span style={{ color: percentChange.startsWith('(+') ? '#16a085' : '#c0392b' }}>
              {percentChange}
            </span>
          ) : null}
        </p>
      </div>

      {/* Trading Modal */}
      <TradingModal
        isOpen={isTradingModalOpen}
        onClose={handleCloseModal}
        symbol={currentSymbol}
        price={price}
        action={tradingAction}
      />

      {/* Symbol Search Modal */}
      {symbolPickerOpen && (
        <SymbolSearchModal
          onSelect={(symbol) => {
            setCurrentSymbol(symbol)
            setSymbolPickerOpen(false)
          }}
          onClose={() => setSymbolPickerOpen(false)}
          title="Select Chart Symbol"
          recentStorageKey="chartRecentSymbols"
          customPopularSymbols={[
            'BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'LTCUSDT',
            'XRPUSDT', 'ADAUSDT', 'DOGEUSDT', 'MATICUSDT', 'AVAXUSDT'
          ]}
        />
      )}

      {/* FX Symbol Picker Modal */}
      {fxPickerOpen && (
        <SymbolSearchModal
          onSelect={(symbol) => {
            setFxSymbol(symbol)
            setFxPickerOpen(false)
          }}
          onClose={() => setFxPickerOpen(false)}
          title="Select Comparison Symbol"
          recentStorageKey="fxCompareRecentSymbols"
          customPopularSymbols={[
            'BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT', 'LTCUSDT',
            'XRPUSDT', 'ADAUSDT', 'DOGEUSDT', 'MATICUSDT', 'AVAXUSDT'
          ]}
        />
      )}

      {/* AI Analysis Modal */}
      <AIAnalysisModal
        isOpen={isAIModalOpen}
        onClose={closeAIModal}
        analysis={aiAnalysis}
        loading={aiLoading}
        error={aiError}
        symbol={currentSymbol}
        timeframe={timeframe}
        currentPrice={lastPrice || undefined}
        onRetry={() => handleAIAnalysis()}
      />
      </div>
      
      {/* Analytics Table - positioned below chart */}
      {showAnalytics && (
        <AnalyticsTable 
          chartData={chartDataLocal}
          currentPrice={price || undefined}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  )
}